/**
 * imageRenderer.js
 * Image enhancement for rendered markdown content and user-uploaded image previews.
 * Wraps <img> elements with interactive containers (overlay actions, loading skeleton)
 * and opens the lightbox on click.
 */

import { openLightbox } from './lightbox.js?v=302';

/* ── SVG Icons (inline, matching project icon style) ───────────── */

const ICON_COPY = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;

const ICON_DOWNLOAD = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>`;

const ICON_CHECK = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>`;

const ICON_EXPAND = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>`;

/** Sentinel attribute to mark an image as already enhanced */
const ENHANCED_ATTR = 'data-media-enhanced';

/**
 * Enhance all <img> elements inside a rendered message bubble.
 * Idempotent: safe to call repeatedly on the same container.
 *
 * @param {HTMLElement} messageEl - The message bubble element (or any container with .message-content)
 */
export function enhanceImages(messageEl) {
  if (!messageEl || !(messageEl instanceof HTMLElement)) return;

  const contentEl = messageEl.querySelector('.message-content') || messageEl;
  const images = contentEl.querySelectorAll('img');
  if (images.length === 0) return;

  /** Collect all image sources for gallery navigation */
  const allSrcs = [];
  const containers = [];

  images.forEach((img) => {
    // Skip already-enhanced images
    if (img.hasAttribute(ENHANCED_ATTR)) {
      allSrcs.push(img.src);
      return;
    }
    img.setAttribute(ENHANCED_ATTR, '');

    allSrcs.push(img.src);

    // Build wrapper
    const container = document.createElement('div');
    container.className = 'media-image-container';

    // Skeleton placeholder
    const skeleton = document.createElement('div');
    skeleton.className = 'media-image-skeleton';
    container.appendChild(skeleton);

    // Insert container in place of img
    img.parentNode.insertBefore(container, img);
    container.appendChild(img);
    img.classList.add('media-image');

    // Fade-in on load
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add('media-image--loaded');
      skeleton.remove();
    } else {
      img.addEventListener('load', () => {
        img.classList.add('media-image--loaded');
        skeleton.remove();
      }, { once: true });
      img.addEventListener('error', () => {
        skeleton.remove();
        container.classList.add('media-image-container--error');
      }, { once: true });
    }

    // Hover overlay with action buttons
    const overlay = document.createElement('div');
    overlay.className = 'media-image-overlay';
    overlay.setAttribute('aria-hidden', 'true');

    overlay.innerHTML = `
      <button class="media-image-action" data-action="expand" title="View full size" aria-label="View full size image">${ICON_EXPAND}</button>
      <button class="media-image-action" data-action="copy" title="Copy image" aria-label="Copy image to clipboard">${ICON_COPY}</button>
      <button class="media-image-action" data-action="download" title="Download image" aria-label="Download image">${ICON_DOWNLOAD}</button>
    `;
    container.appendChild(overlay);

    // Overlay action handlers — read src from live DOM, not closure
    overlay.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      // Get the live img element from the container (not the closure ref)
      const liveImg = container.querySelector('img');
      const src = liveImg ? liveImg.src : img.src;

      const action = btn.dataset.action;
      if (action === 'copy') {
        copyImageToClipboard(src, btn);
      } else if (action === 'download') {
        downloadImage(src);
      } else if (action === 'expand') {
        window.open(src, '_blank', 'noopener,noreferrer');
      }
    });

    containers.push(container);
  });
}

/**
 * Build an HTML string of small thumbnail previews for user-uploaded image files.
 *
 * @param {Array<{name: string, type: string, dataUrl?: string, url?: string}>} files
 * @returns {string} HTML fragment
 */
export function renderUserImagePreviews(files) {
  if (!files || files.length === 0) return '';

  const imageFiles = files.filter((f) => {
    const t = (f.type || '').toLowerCase();
    return t === 'image' || t.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp|ico|avif)$/i.test(f.name || '');
  });

  if (imageFiles.length === 0) return '';

  const thumbs = imageFiles.map((file, i) => {
    const src = file.dataUrl || file.url || '';
    if (!src) return '';
    return `<div class="media-thumb" data-index="${i}" role="button" tabindex="0" aria-label="View ${escapeAttr(file.name || 'image')}">
      <img class="media-thumb-img" src="${escapeAttr(src)}" alt="${escapeAttr(file.name || 'Uploaded image')}" loading="lazy" decoding="async" />
    </div>`;
  }).filter(Boolean).join('');

  if (!thumbs) return '';

  return `<div class="media-thumb-strip">${thumbs}</div>`;
}

/* ── Internal helpers ──────────────────────────────────────────── */

/**
 * Copy an image to the clipboard by drawing it to a canvas and producing a PNG blob.
 * Falls back to copying the URL if the canvas approach fails (CORS, etc.).
 *
 * @param {string} src - Image URL
 * @param {HTMLElement} btn - The action button (for visual feedback)
 */
async function copyImageToClipboard(src, btn) {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (blob && navigator.clipboard && navigator.clipboard.write) {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
    } else {
      // Fallback: copy URL
      await navigator.clipboard.writeText(src);
    }
  } catch {
    // Last-resort fallback: copy URL as text
    try {
      await navigator.clipboard.writeText(src);
    } catch { /* silent */ }
  }

  // Visual feedback
  if (btn) {
    const original = btn.innerHTML;
    btn.innerHTML = ICON_CHECK;
    btn.classList.add('media-image-action--success');
    setTimeout(() => {
      btn.innerHTML = original;
      btn.classList.remove('media-image-action--success');
    }, 1800);
  }
}

/**
 * Trigger a download for the given image URL.
 * Fetches as blob to force a real download (cross-origin safe).
 *
 * @param {string} src - Image URL
 */
async function downloadImage(src) {
  try {
    const resp = await fetch(src, { mode: 'cors' });
    if (!resp.ok) throw new Error('fetch failed');
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filenameFromUrl(src);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: open in new tab so user can right-click > Save As
    window.open(src, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Extract a reasonable filename from a URL, with a fallback.
 *
 * @param {string} url
 * @returns {string}
 */
function filenameFromUrl(url) {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (last && /\.\w+$/.test(last)) return decodeURIComponent(last);
  } catch { /* ignore */ }
  return 'image.png';
}

/**
 * Escape a string for safe use inside an HTML attribute.
 *
 * @param {string} str
 * @returns {string}
 */
function escapeAttr(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
